import { createApp, h, ref } from 'vue'
import katex from 'katex'
import { marked } from 'marked'

// Tooltip state manager
const tooltipInstances = new WeakMap()

// Create tooltip element
function createTooltipElement() {
  const el = document.createElement('div')
  el.className = 'tp-tooltip-container'
  document.body.appendChild(el)
  return el
}

// Get or create tooltip container
let tooltipContainer = null
function getTooltipContainer() {
  if (!tooltipContainer) {
    tooltipContainer = createTooltipElement()
  }
  return tooltipContainer
}

// Format content with KaTeX and Markdown
function formatContent(text) {
  if (!text) return ''
  
  let result = text
  
  // Protect and render math expressions
  const mathBlocks = []
  
  // Display math: $$...$$
  result = result.replace(/\$\$([\s\S]+?)\$\$/g, (match, math) => {
    try {
      const rendered = katex.renderToString(math.trim(), { displayMode: true, throwOnError: false })
      mathBlocks.push(rendered)
      return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
    } catch (e) {
      mathBlocks.push(match)
      return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
    }
  })
  
  // Inline math: $...$
  result = result.replace(/\$([^\$\n]+?)\$/g, (match, math) => {
    try {
      const rendered = katex.renderToString(math.trim(), { displayMode: false, throwOnError: false })
      mathBlocks.push(rendered)
      return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
    } catch (e) {
      mathBlocks.push(match)
      return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
    }
  })
  
  // Apply markdown
  result = marked.parse(result, { breaks: true, gfm: true })
  
  // Restore math blocks
  mathBlocks.forEach((block, index) => {
    result = result.replace(`%%MATH_BLOCK_${index}%%`, block)
  })
  
  return result
}

// Parse directive value
function parseValue(value) {
  if (typeof value === 'string') {
    return { text: value, placement: 'top', variant: 'dark', icon: null, delay: 200, html: false }
  }
  return {
    text: value.text || value.content || '',
    placement: value.placement || value.position || 'top',
    variant: value.variant || value.theme || 'dark',
    icon: value.icon || null,
    delay: value.delay ?? 200,
    html: value.html ?? true // Enable HTML/KaTeX by default
  }
}

// Create tooltip DOM
function createTooltip(options) {
  const tooltip = document.createElement('div')
  tooltip.className = `tp-tooltip tp-tooltip--${options.placement} tp-tooltip--${options.variant}`
  tooltip.setAttribute('role', 'tooltip')
  
  const content = options.html ? formatContent(options.text) : escapeHtml(options.text)
  
  tooltip.innerHTML = `
    <div class="tp-tooltip__content">
      ${options.icon ? `<svg class="tp-tooltip__icon" width="14" height="14"><use href="#icon-${options.icon}"></use></svg>` : ''}
      <span class="tp-tooltip__text">${content}</span>
    </div>
    <div class="tp-tooltip__arrow"></div>
  `
  return tooltip
}

// Escape HTML for plain text mode
function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Position tooltip
function positionTooltip(tooltip, target, placement, offset = 8) {
  const targetRect = target.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const scrollX = window.scrollX
  const scrollY = window.scrollY

  let top = 0
  let left = 0
  let arrowStyle = ''

  switch (placement) {
    case 'top':
      top = targetRect.top + scrollY - tooltipRect.height - offset
      left = targetRect.left + scrollX + (targetRect.width - tooltipRect.width) / 2
      arrowStyle = 'bottom: -4px; left: 50%; transform: translateX(-50%) rotate(45deg);'
      break
    case 'bottom':
      top = targetRect.bottom + scrollY + offset
      left = targetRect.left + scrollX + (targetRect.width - tooltipRect.width) / 2
      arrowStyle = 'top: -4px; left: 50%; transform: translateX(-50%) rotate(45deg);'
      break
    case 'left':
      top = targetRect.top + scrollY + (targetRect.height - tooltipRect.height) / 2
      left = targetRect.left + scrollX - tooltipRect.width - offset
      arrowStyle = 'right: -4px; top: 50%; transform: translateY(-50%) rotate(45deg);'
      break
    case 'right':
      top = targetRect.top + scrollY + (targetRect.height - tooltipRect.height) / 2
      left = targetRect.right + scrollX + offset
      arrowStyle = 'left: -4px; top: 50%; transform: translateY(-50%) rotate(45deg);'
      break
  }

  // Boundary checks
  const padding = 10
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  if (left < padding) left = padding
  if (left + tooltipRect.width > viewportWidth - padding) {
    left = viewportWidth - tooltipRect.width - padding
  }
  if (top < scrollY + padding) {
    // Flip to bottom if not enough space on top
    if (placement === 'top') {
      top = targetRect.bottom + scrollY + offset
      arrowStyle = 'top: -4px; left: 50%; transform: translateX(-50%) rotate(45deg);'
      tooltip.classList.remove('tp-tooltip--top')
      tooltip.classList.add('tp-tooltip--bottom')
    }
  }

  tooltip.style.top = `${top}px`
  tooltip.style.left = `${left}px`
  
  const arrow = tooltip.querySelector('.tp-tooltip__arrow')
  if (arrow) {
    arrow.style.cssText = arrowStyle
  }
}

// Show tooltip
function showTooltip(el, options) {
  const instance = tooltipInstances.get(el)
  if (!instance || instance.visible) return

  instance.showTimeout = setTimeout(() => {
    const tooltip = createTooltip(options)
    tooltip.style.opacity = '0'
    document.body.appendChild(tooltip)
    
    // Position after adding to DOM
    requestAnimationFrame(() => {
      positionTooltip(tooltip, el, options.placement)
      tooltip.style.opacity = '1'
    })

    instance.tooltip = tooltip
    instance.visible = true
  }, options.delay)
}

// Hide tooltip
function hideTooltip(el) {
  const instance = tooltipInstances.get(el)
  if (!instance) return

  clearTimeout(instance.showTimeout)
  
  if (instance.tooltip) {
    instance.tooltip.style.opacity = '0'
    setTimeout(() => {
      if (instance.tooltip && instance.tooltip.parentNode) {
        instance.tooltip.parentNode.removeChild(instance.tooltip)
      }
      instance.tooltip = null
      instance.visible = false
    }, 150)
  }
}

// Directive definition
export const vTooltip = {
  mounted(el, binding) {
    const options = parseValue(binding.value)
    
    if (!options.text) return

    const instance = {
      options,
      tooltip: null,
      visible: false,
      showTimeout: null
    }

    tooltipInstances.set(el, instance)

    el._tooltipMouseEnter = () => showTooltip(el, options)
    el._tooltipMouseLeave = () => hideTooltip(el)
    el._tooltipFocus = () => showTooltip(el, options)
    el._tooltipBlur = () => hideTooltip(el)

    el.addEventListener('mouseenter', el._tooltipMouseEnter)
    el.addEventListener('mouseleave', el._tooltipMouseLeave)
    el.addEventListener('focus', el._tooltipFocus)
    el.addEventListener('blur', el._tooltipBlur)
  },

  updated(el, binding) {
    const instance = tooltipInstances.get(el)
    if (instance) {
      instance.options = parseValue(binding.value)
    }
  },

  unmounted(el) {
    hideTooltip(el)
    tooltipInstances.delete(el)

    el.removeEventListener('mouseenter', el._tooltipMouseEnter)
    el.removeEventListener('mouseleave', el._tooltipMouseLeave)
    el.removeEventListener('focus', el._tooltipFocus)
    el.removeEventListener('blur', el._tooltipBlur)
  }
}

export default vTooltip
